/**
 * BuyProductPage — public buy page for any member's digital product.
 * Paths:
 *   /buy/:pid          → product page with Buy button
 *   /buy/:pid/success  → after Stripe redirect, polls status + shows download link
 *
 * Buyer flow:
 *   1. Visit /buy/:pid
 *   2. Click "Buy Now · $X"  →  Stripe Checkout
 *   3. Stripe redirects to /buy/:pid/success?session_id=XXX
 *   4. Page polls /buy/:pid/status/:session_id every 2s
 *   5. When paid, shows a 7-day tokenized download link
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import SiteNav from "../components/SiteNav";
import { Download, Lock, ArrowRight, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const RED = "#C9202E";
const CREAM = "#FFFFFF";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function BuyProductPage() {
  const { pid } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Success-state vars
  const isSuccess = window.location.pathname.includes("/success");
  const [paidInfo, setPaidInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  // Load product
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/digital-products/${pid}/public`);
        setProduct(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Product not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [pid]);

  // On success page, poll for paid status
  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let cancelled = false;
    const poll = async (n = 0) => {
      if (cancelled || n >= 12) return;
      try {
        const { data } = await api.get(`/digital-buy/${pid}/status/${sessionId}`);
        if (data?.paid) { setPaidInfo(data); return; }
      } catch { /* ignore */ }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [isSuccess, sessionId, pid]);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/digital-products/${pid}/checkout`, { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <SiteNav />
        <div className="text-foreground/60">Loading…</div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-screen" style={{ background: CREAM }}>
        <SiteNav />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-serif-display text-4xl text-red-wine">Product not found</h1>
          <Link to="/" className="mt-6 inline-block text-red-wine underline">Back home</Link>
        </div>
      </div>
    );
  }

  // ─────── SUCCESS / DOWNLOAD VIEW ───────
  if (isSuccess) {
    return (
      <div className="min-h-screen" style={{ background: CREAM }}>
        <SiteNav />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="buy-success-page">
          {!paidInfo ? (
            <>
              <h1 className="font-serif-display text-4xl text-red-wine">Confirming payment…</h1>
              <p className="text-foreground/70 mt-3">Attempt {attempts + 1} of 12</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: "#2E9F3A" }} />
              <div className="text-[11px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: GOLD }}>★ Amen! Payment Received</div>
              <h1 className="font-serif-display text-4xl text-red-wine">Your Download Is Ready</h1>
              <div className="h-px w-16 mx-auto mt-4" style={{ background: GOLD }} />
              <p className="text-base text-foreground mt-5">
                Thanks for buying <b>{product.name}</b>. Click below to download your file.
              </p>

              <a
                href={`${BACKEND}${paidInfo.download_url}`}
                className="mt-7 inline-flex items-center gap-3 px-8 py-4 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-2xl text-white hover:-translate-y-0.5 transition"
                style={{ background: RED }}
                data-testid="buy-success-download"
                download
              >
                <Download className="w-5 h-5" /> Download {product.file_name}
              </a>

              <div className="mt-5 text-xs text-foreground/60 max-w-md mx-auto">
                Link valid for <b>7 days</b> · up to <b>{paidInfo.max_downloads || 5} downloads</b>.
                Bookmark it or save the file somewhere safe.
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─────── PRODUCT / BUY VIEW ───────
  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <SiteNav />

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid md:grid-cols-[260px_1fr] gap-8 items-start">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name}
                 className="w-full aspect-square object-contain p-4 rounded-2xl border-4 shadow-2xl bg-white"
                 style={{ borderColor: GOLD }} />
          ) : (
            <div className="w-full aspect-square rounded-2xl border-4 shadow-2xl flex items-center justify-center"
                 style={{ background: "#F4ECD8", borderColor: GOLD }}>
              <FileText className="w-20 h-20 text-foreground/30" />
            </div>
          )}

          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>
              ★ {product.seller_full_name || product.seller_username || "A Faitharama Builder"}
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-red-wine uppercase mt-1 leading-tight">
              {product.name}
            </h1>
            <div className="h-px w-16 mt-3" style={{ background: GOLD }} />
            {product.description && (
              <p className="text-base md:text-lg text-foreground mt-5 leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>
            )}

            <div className="mt-8 inline-flex items-baseline gap-3">
              <span className="font-serif-display text-6xl text-red-wine">${product.price_usd}</span>
              <span className="text-xs uppercase tracking-wider text-foreground/60">one-time · instant download</span>
            </div>

            <button
              onClick={buy}
              disabled={busy}
              className="mt-5 inline-flex items-center gap-3 px-8 py-4 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-2xl text-white hover:-translate-y-0.5 transition disabled:opacity-60"
              style={{ background: RED }}
              data-testid="buy-now-button"
            >
              {busy ? "Opening Checkout…" : "★ Buy Now"}
              <ArrowRight className="w-5 h-5" />
            </button>

            <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground/60">
              <Lock className="w-3 h-3" /> Secure checkout via Stripe · Instant delivery
            </div>

            <div className="mt-8 text-xs text-foreground/50">
              File: {product.file_name} · {product.file_size_mb} MB · Downloaded {product.downloads_total || 0} times.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
